import { useGetEnergyDataQuery } from '@/api/energy-analysis/energyDataApiSlice';
import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import {
  buildTreeData,
  getChildren,
} from '@/features/energyxplorer/utils/energyTreeFunctions';
import { isOutOfBounds } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export function useNodeDetailedTimestampsSummary(
  accountId,
  selectedEnergyGroup,
  nodeId,
) {
  const timeRange = useSelector(selectTimeRange);
  const { data: energyData } = useGetEnergyDataQuery({
    accountId: accountId,
    energyGroupId: selectedEnergyGroup?.energyGroupId,
    start: DateTime.now().startOf('day').toMillis(),
    end: DateTime.now().endOf('day').toMillis(),
    activePeriodFilter: undefined,
    activeDaysFilter: undefined,
    activeHolidaysFilter: undefined,
  });

  const enhancedEnergyGroup = useMemo(() => {
    if (!selectedEnergyGroup || !energyData?.NodeEnergySummaries)
      return selectedEnergyGroup;

    const summaryMap = energyData?.NodeEnergySummaries.reduce(
      (acc, summary) => {
        acc[summary.Id] = summary;
        return acc;
      },
      {},
    );

    const enhancedNodes = selectedEnergyGroup?.energyTreeNodes?.map((node) => ({
      ...node,
      energySummary: summaryMap[node.energyTreeNodeId] || null,
    }));

    return {
      ...selectedEnergyGroup,
      energyTreeNodes: enhancedNodes,
    };
  }, [selectedEnergyGroup, energyData?.NodeEnergySummaries]);

  const nodeTree = useMemo(() => {
    if (!enhancedEnergyGroup) return null;

    const treeData = buildTreeData(enhancedEnergyGroup?.energyTreeNodes);

    const nodeToAnalyze = treeData.find(
      (node) => node.energyTreeNodeId === nodeId,
    );
    return nodeToAnalyze && treeData.length
      ? buildNodeTree(nodeToAnalyze, treeData, timeRange)
      : null;
  }, [enhancedEnergyGroup, nodeId, timeRange]);

  if (!nodeTree) return null;
  return nodeTree;
}
// Recursive function to build the tree structure for a given node
function buildNodeTree(node, allNodes, timeRange) {
  const children = getChildren(node.energyTreeNodeId, allNodes);
  return {
    id: node.energyTreeNodeId,
    label: node.name || node.label || `Node ${node.energyTreeNodeId}`,
    children: children?.length
      ? children?.map((child) => buildNodeTree(child, allNodes, timeRange))
      : undefined,
    isOutOfBounds:
      node?.energySummary &&
      isOutOfBounds(node?.energySummary?.TimestampsSummary, timeRange),
    ...node,
  };
}
